import styled, { css } from 'styled-components';

import { hexToRGB } from '@/styles/helpers';

export interface ScModalSelectProps {
  alignBottom?: boolean;
  bottom?: number;
  padding?: string;
  gap?: number;
  isHidden?: boolean;
}

export const ScModalSelect = styled.div<ScModalSelectProps>`
  position: fixed;
  inset: 0;
  bottom: ${({ bottom }) => (bottom ? `${bottom}px` : `0`)};
  background-color: ${({ theme }) => hexToRGB(theme.blackColor, 0.95)};
  padding: ${({ padding }) => padding || '16px 0'};
  z-index: 1120;
  display: flex;
  visibility: ${({ isHidden }) => (isHidden ? 'hidden' : 'initial')};
  flex-direction: column;
  gap: ${({ gap }) => (gap != null ? `${gap}px` : '32px')};
  overscroll-behavior: contain;

  ${({ alignBottom }) =>
    alignBottom &&
    css`
      align-items: flex-start;
      justify-content: space-between;
    `}
`;

export const ScModalSelectHeader = styled.div`
  padding: 0 16px;

  .header {
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
    gap: 8px;

    & > .icon {
      width: 40px;
      height: 40px;
      display: flex;
      justify-content: center;
      align-items: center;
      object-fit: contain;
    }

    & > .title {
      font-size: 16px;
      line-height: 24px;
      color: ${({ theme }) => theme.whiteColor};
      margin-right: 40px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

export const ScSelectSearchMob = styled.div`
  display: flex;
  width: calc(100% - 32px);
  margin: 0 auto 24px;
  padding: 0 16px;

  input {
    font-size: 16px;
    line-height: 24px;
    color: ${({ theme }) => theme.whiteColor};
    background-color: transparent;
    border-bottom: ${({ theme }) =>
      `1px solid ${hexToRGB(theme.whiteColor, 0.2)}`};
    border-radius: 0;
    width: 100%;
    min-width: 0;
    height: 40px;
    padding: 0 16px;
  }
`;

export interface ScModalSelectBodyProps {
  innerAlignBottom?: boolean;
  isEstonia?: boolean;
}

export const ScModalSelectBody = styled.div<ScModalSelectBodyProps>`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 0 16px 16px;

  ${({ innerAlignBottom, isEstonia }) =>
    (innerAlignBottom || isEstonia) &&
    css`
      margin-top: auto;

      & > * {
        width: 100%;
      }
    `}

  .not-found {
    text-align: center;
    font-size: 16px;
    line-height: 24px;
    color: ${({ theme }) => theme.whiteColor};
  }
`;

export const ScModalSelectRow = styled.div<{ cols: number; rowGap?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ cols }) => cols}, 1fr);
  grid-column-gap: 16px;
  grid-row-gap: ${({ rowGap }) => `${rowGap}px`};
  overflow-y: auto;
`;

interface ScModalSelectOptionProps {
  roundIcon?: boolean;
  isActive?: boolean;
}

export const ScModalSelectOption = styled.button<ScModalSelectOptionProps>`
  background-color: transparent;
  border: ${({ theme }) => `1px solid ${hexToRGB(theme.whiteColor, 0.2)}`};
  width: 100%;
  min-width: 0;
  height: 40px;
  border-radius: 24px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  padding: 0 4px 0 8px;
  background-color: ${({ isActive, theme }) =>
    isActive ? theme.whiteColor : theme.blackColor};
  position: relative;

  & > .icon {
    margin-right: 8px;
    img {
      width: 24px;
      height: 24px;
      object-fit: cover;
      object-position: center;
      border-radius: ${({ roundIcon }) => (roundIcon ? '50%' : '0px')};
    }
  }

  & > svg {
    width: 32px;
    height: 32px;
    position: absolute;
    right: 4px;
  }

  & > .name {
    font-size: 16px;
    line-height: 24px;
    color: ${({ theme, isActive }) =>
      isActive ? theme.blackColor : theme.blackLightColor};
    white-space: nowrap;
    text-align: left;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

export const ScModalSelectFooter = styled.div<ScModalSelectBodyProps>`
  padding: 16px 16px 0;
  ${({ innerAlignBottom }) =>
    !innerAlignBottom &&
    css`
      margin-top: auto;
    `}
`;
